/**
 * What a flow snapshot tells the host page (`EmbedScreen`), in the published
 * `trustsy:*` contract (`./index`).
 */
import { stepFor } from '../flow/step';
import type { CollectionFlowSnapshot } from '../flow/types';
import { isSecureOrigin, type EmbedMessage } from './index';

/**
 * The outcome message once the applicant's part is over, else `null`.
 *
 * A review step is `manual_review`. A terminal step says `approved` or
 * `rejected` only when it carries that verdict tag; a flow that ended without
 * one is `manual_review` too — "not decided here", the partner's webhook is the
 * record. A failed run is an error, not an outcome: the contract's statuses are
 * published and stay exactly three.
 */
export function doneMessage(snapshot: CollectionFlowSnapshot): EmbedMessage | null {
  const step = stepFor(snapshot);
  if (step.kind === 'review') return { type: 'trustsy:done', status: 'manual_review' };
  if (step.kind !== 'terminal') return null;
  switch (step.outcome) {
    case 'approved':
      return { type: 'trustsy:done', status: 'approved' };
    case 'rejected':
      return { type: 'trustsy:done', status: 'rejected' };
    case 'failed':
      return { type: 'trustsy:error', code: 'flow_failed' };
    default:
      return { type: 'trustsy:done', status: 'manual_review' };
  }
}

/**
 * The host page's exact origin from `?origin=`, or `null` when it is not one:
 * `https://…`, or `http://` on localhost for development, with no path, query
 * or credentials. `postMessage` delivers only to a parent of exactly this
 * origin, so a wrong value drops messages rather than leaking them.
 */
export function embedOrigin(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  return isSecureOrigin(url) && url.origin === raw.replace(/\/$/, '') ? url.origin : null;
}
